import {
  Form,
  FormikConfig,
  FormikErrors,
  FormikProps,
  FormikProvider,
  useFormik,
  yupToFormErrors,
} from 'formik';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  BubbleAlert,
  BubbleBadge,
  BubbleButton,
  BubbleCurrency,
  BubbleFormGroup,
  BubbleInput,
  BubbleOverlay,
  BubbleCheckboxGroup,
  BubbleSelect,
  BubbleTable,
  BubbleTextArea,
} from '../BubbleUI';
import * as Yup from 'yup';
import { groupBy, mapBy, notFalsy, notUndefined } from '@go-shared/lib/functional';
import { ObjectId } from 'bson';
import { useBubbleDrawerTools } from '../BubbleUI/BubbleDrawer';
import { CUSTOM_ICONS } from '@go-client/lib/icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  fme,
  isValidEmail,
  keyFromLabel,
  formatCentsToUSD,
  safeIdCompare,
} from '@go-shared/lib/utils';
import { Time } from '@go-shared/types/time';
import phone from 'phone';
import {
  Reservation,
  ReservationAnalytics,
  ReservationPayment,
  ReservationPendingPayment,
} from '@go-shared/types/reservation';
import { ReserveTimelineItem } from '@go-shared/types/reserve';
import {
  dateToIsoUTC,
  getLocaleDateTimeStringLocal,
  isoToDateUTC,
  isIsoDate,
} from '@go-shared/lib/time';
import {
  ReservableAddon,
  ReservableEventWithTimeslots,
  ReservablePackage,
  ReservableQuestionField,
  ReservableTicket,
} from '@go-shared/types/reservable';
import ReservationQuestionFiller from '../ReservationQuestionFiller';
import { Dropdown, DropdownItem } from '@spark-labs/avocado-ui';
import BubbleTooltip from '../BubbleUI/BubbleTooltip';
import ReservationAddonFiller from '../ReservationAddonFiller';
import ReservationPackageFiller from '../ReservationPackageFiller';
import ReservationDateTimeFiller from '../ReservationDateTimeFiller';
import {
  createManualPayment,
  deleteManualPayment,
  openTerminalPayment,
  showTerminalDisplay,
  cancelTerminalPayment,
  changePaymentNotes,
  getRelatedReservations,
} from '@go-client/lib/api/reservation';
// @ts-ignore
import QRCode from 'qrcode';
import {
  addGlyphsToPayment,
  buildReserveSlotKey,
  calculateReservationAnalytics,
  calculateReservationPricing,
  experienceIsRental,
  getReservationAvailableFundsForRefund,
  getReservationRevenue,
  getTicketPriceAfterOverrides,
  ReservePaymentMethod,
  ReservePaymentType,
  slotKeyFromReservation,
  summarizeOccurrences,
  buildDayspanSubsequentSlots,
  DaySpannableSlot,
  checkReservationErrors,
} from '@go-shared/lib/reserve';
import {
  BubbleTableFieldBehavior,
  BubbleTableFields,
  BubbleTableTemplate,
} from '../BubbleUI/BubbleTable';
import { PricingLineItem } from '@go-shared/lib/reserve';
import { camelToSentenceCase, capitalize } from '@go-shared/lib/string';
import { ReservableEventTemplateSnapshot } from '@go-shared/types/reservable';
import { CopyToClipboardButton } from '../CopyToClipboard';
import { DefaultedText } from '../DefaultedText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { getHourFormat } from '@go-shared/lib/property';
import { ReserveScheduleSlotPriceOverrides } from '@go-shared/types/reserveSchedule';
import { findReservableDetails } from '@go-client/lib/api/reservable';
import { canManageReservation } from '@go-shared/lib/permissions';
import { sendConfirmationEmail } from '@go-client/lib/api/reservation';

import './ReservationFormik.scss';
import { SimpleUser } from '@go-shared/types/user';
import { usePublicServerInfo } from '@go-client/context/PublicServerInfoContext';

export interface ReservationFormikProps {
  type: 'create' | 'edit' | 'view' | 'filter';
  defaultView?: 'details' | 'payments';
  users: SimpleUser[];
  selectedEventSnapshot: ReservableEventTemplateSnapshot;
  onRefreshReservation?: (reservation: Reservation) => void;
  onNavigateToReservation?: (reservation: Reservation) => void;
  reservation?: Reservation;
  extManagedFormik?: FormikProps<ReservationFormikForm>;
  actionsElem?: HTMLDivElement;
  actionsClassName?: string;
  actionsAppearance?: 'drawer-float';
  subnavClassName?: string;
}

export interface ReservationFormikRef {
  reset: () => void;
  submit: () => void;
}

function GENERATE_INITIAL_VALUES(
  initialDate: string,
  slot?: ReserveTimelineItem,
  reservation?: Reservation,
  eventSnapshot?: ReservableEventTemplateSnapshot
): ReservationFormikForm {
  const initialValues: ReservationFormikForm = {
    date: slot?.date
      ? dateToIsoUTC(slot.date)
      : reservation
      ? dateToIsoUTC(reservation.date)
      : initialDate,
    time: slot?.time ?? reservation?.time,
    timeDisplay: slot?.timeDisplay ?? reservation?.timeDisplay,
    duration: slot?.duration ?? reservation?.duration,
    host: slot?.host ?? reservation?.host,
    rental: slot?.rental ?? reservation?.rental,
    fields: reservation?.fields ?? {},
    customer: {
      firstName: reservation?.customer.firstName ?? '',
      lastName: reservation?.customer.lastName ?? '',
      email: reservation?.customer.email ?? '',
      phone: reservation?.customer.phone ?? '',
    },
    tickets: (reservation?.tickets ?? [])
      .map(t => {
        const relevantTicket = reservation
          ? reservation.eventSnapshot.reservables.tickets.find(et => et._id === t.ticketId)
          : eventSnapshot?.reservables.tickets.find(et => et._id === t.ticketId);
        if (!relevantTicket) return undefined;

        const ticketKeys = relevantTicket?.fields.map(item => keyFromLabel(item.label));
        let values: { [key: string]: string } = { ...t.values };

        ticketKeys?.forEach(key => {
          if (!values[key]) {
            values[key] = '';
          }
        });

        return {
          _id: t._id,
          ticketId: t.ticketId,
          values,
          _ticket: relevantTicket,
        };
      })
      .filter(notUndefined),
    packages: (reservation?.packages ?? [])
      .map(p => {
        const relevantPackage = reservation
          ? reservation.eventSnapshot.reservables.packages.find(ep => ep._id === p.packageId)
          : eventSnapshot?.reservables.packages.find(ep => ep._id === p.packageId);
        if (!relevantPackage) return undefined;

        return {
          _id: p._id,
          packageId: p.packageId,
          quantity: p.quantity,
          _package: relevantPackage,
        };
      })
      .filter(notUndefined),
    addons: (reservation?.addons ?? [])
      .map(a => {
        const relevantAddon = reservation
          ? reservation.eventSnapshot.reservables.addons.find(ad => ad._id === a.addonId)
          : eventSnapshot?.reservables.addons.find(ad => ad._id === a.addonId);
        if (!relevantAddon) return undefined;

        return {
          _id: a._id,
          addonId: a.addonId,
          quantity: a.quantity,
          _addon: relevantAddon,
        };
      })
      .filter(notUndefined),
    payments: [],
    priceOverrides: slot?.priceOverrides ?? reservation?.priceOverrides,
    additionalOccurrences: (reservation?.additionalOccurrences ?? []).map(occ => ({
      ...occ,
      _id: new ObjectId().toString(),
      date: dateToIsoUTC(occ.date),
    })),
    _fields: reservation
      ? reservation.eventSnapshot.reservables.fields
      : eventSnapshot?.reservables.fields ?? [],
    _slot: slot,
  };

  return initialValues;
}

interface ReservationFormikTickets {
  _id: string;
  ticketId: string;
  values: {
    [key: string]: string;
  };
  _ticket: ReservableTicket;
}

interface ReservationFormikPackages {
  _id: string;
  packageId: string;
  quantity: number;
  _package: ReservablePackage;
}

interface ReservationFormikAddons {
  _id: string;
  addonId: string;
  quantity: number;
  _addon: ReservableAddon;
}

interface ReservationFormikPayment {
  _id: string;
  method: string;
  amount: number;
  externalId: string;
}

export interface ReservationFormikOccurrence {
  _id?: string; // stable client-side key for additional occurrences, not persisted
  date: string;
  time: Time | undefined;
  timeDisplay?: string;
  duration?: number;
  host?: string;
  rental?: string;
}

export function dayspanSlotToFormikOccurrence(
  spanSlot: DaySpannableSlot
): ReservationFormikOccurrence {
  return {
    _id: new ObjectId().toString(),
    date: dateToIsoUTC(spanSlot.date),
    time: spanSlot.time,
    timeDisplay: spanSlot.timeDisplay,
    duration: spanSlot.duration,
    host: spanSlot.host,
    rental: spanSlot.rental,
  };
}

export type ReservationFormikForm = ReservationFormikOccurrence & {
  customer: {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
  };
  payments: ReservationFormikPayment[];
  tickets: ReservationFormikTickets[];
  packages: ReservationFormikPackages[];
  addons: ReservationFormikAddons[];
  fields: {
    [key: string]: string;
  };
  priceOverrides?: ReserveScheduleSlotPriceOverrides;
  additionalOccurrences?: ReservationFormikOccurrence[];
  _fields: ReservableQuestionField[];
  _slot?: ReserveTimelineItem;
};

export function buildFormik(
  initialDate: string,
  slot?: ReserveTimelineItem,
  reservation?: Reservation,
  eventSnapshot?: ReservableEventTemplateSnapshot,
  onSubmit?: FormikConfig<ReservationFormikForm>['onSubmit']
) {
  const formik = useFormik({
    initialValues: GENERATE_INITIAL_VALUES(initialDate, slot, reservation, eventSnapshot),
    validate: async values => {
      let errors: FormikErrors<ReservationFormikForm> = {};

      function validateQuestionFields(
        fieldDefs: ReservableQuestionField[],
        fieldValues: Record<string, string>,
        pathPrefix: string,
        createError: Yup.TestContext['createError']
      ): Yup.ValidationError | true {
        for (const field of fieldDefs) {
          const fieldKey = keyFromLabel(field.label);
          const fieldValue = fieldValues[fieldKey];

          if (field.required && !fieldValue) {
            return createError({
              path: `${pathPrefix}.${fieldKey}`,
              message: `${field.label} is required`,
            });
          }

          if (!fieldValue || field.type !== 'input') continue;

          if (field.settings.type === 'number' && !/^\d+$/.test(fieldValue)) {
            return createError({
              path: `${pathPrefix}.${fieldKey}`,
              message: `${field.label} must contain numbers only`,
            });
          }

          if (field.settings.type === 'email' && !isValidEmail(fieldValue)) {
            return createError({
              path: `${pathPrefix}.${fieldKey}`,
              message: `${field.label} must be a valid email`,
            });
          }

          if (field.settings.type === 'phone' && !phone(fieldValue).isValid) {
            return createError({
              path: `${pathPrefix}.${fieldKey}`,
              message: `${field.label} must be a valid phone number`,
            });
          }
        }

        return true;
      }

      const reservationFieldSchema = Yup.object().test(
        'validate-question-fields',
        'Invalid field values',
        function (values) {
          const fields = this.parent._fields;
          if (!fields) return true;

          const fieldValues = (values as Record<string, string>) || {};

          return validateQuestionFields(fields, fieldValues, 'fields', this.createError);
        }
      );

      const ticketFieldSchema = Yup.object().shape({
        values: Yup.object().test(
          'validate-ticket-fields',
          'Invalid ticket values',
          function (values) {
            const ticket = this.parent._ticket as ReservableTicket;
            if (!ticket) return true;

            const ticketValues = (values as Record<string, string>) || {};
            const ticketIdx = (this.options as any).index;

            const pathPrefix =
              typeof ticketIdx === 'number' ? `tickets[${ticketIdx}].values` : this.path;

            return validateQuestionFields(
              ticket.fields,
              ticketValues,
              pathPrefix,
              this.createError
            );
          }
        ),
      });

      const basicSchema = Yup.object({
        date: Yup.string().required('Date is required'),
        time: Yup.object()
          .shape({
            hour: Yup.number().min(0).max(23).required(),
            minute: Yup.number().min(0).max(59).required(),
          })
          .required('Time is required'),
        customer: Yup.object().shape({
          firstName: Yup.string().required("Customer's first name is required"),
          lastName: Yup.string().required("Customer's last name is required"),
          email: Yup.string()
            .email("Customer's email must be a valid email")
            .required("Customer's email is required"),
          phone: Yup.string().test('is-valid-phone', 'Please enter a valid phone number', value => {
            if (!value) return false;
            const phoneResult = phone(value);
            return phoneResult.isValid;
          }),
        }),
        fields: reservationFieldSchema,
        tickets: Yup.array().of(ticketFieldSchema).min(1, 'At least one ticket is required'),
      });

      try {
        await basicSchema.validate(values, { abortEarly: false });
      } catch (err) {
        const t = yupToFormErrors<ReservationFormikForm>(err);
        errors = { ...errors, ...t };
      }

      return errors;
    },
    onSubmit: onSubmit || (() => {}),
  });

  return formik;
}

export function resetFormik(
  initialDate: string,
  formik: FormikProps<ReservationFormikForm>,
  slot?: ReserveTimelineItem,
  reservation?: Reservation,
  eventSnapshot?: ReservableEventTemplateSnapshot
) {
  formik.resetForm({
    values: GENERATE_INITIAL_VALUES(initialDate, slot, reservation, eventSnapshot),
  });
}

interface PricingBreakdownTableItem {
  id: string;
  name: string;
  type?: string;
  price: string;
  quantity: number;
  total: string;
  totalAsNum: number;
  checked: boolean;
  _item: PricingLineItem;
}

interface PaymentLedgerTableItem {
  id: string;
  type: string;
  method: string;
  amount: string;
  date: string;
  externalId: string;
  systemLog: string;
  notes: string;
  _item: ReservationPayment;
}

interface PendingPaymentTableItem {
  id: string;
  amount: string;
  date: string;
  url: string;
  _item: ReservationPendingPayment;
}

type ReservationFormikTab = 'details' | 'payments' | 'cart';
type ReservationFormikSubTab =
  | 'none'
  | 'date-time'
  | 'tickets'
  | 'packages'
  | 'addons'
  | 'reservation-questions';

interface ReservationPaymentForm {
  type: ReservePaymentType;
  method: ReservePaymentMethod;
  amount: number;
  notes: string;
}

function ReservationFormik(
  props: ReservationFormikProps,
  ref: React.ForwardedRef<ReservationFormikRef>
) {
  const { serverDate } = usePublicServerInfo();

  const selectedEvent = props.reservation
    ? props.reservation.eventSnapshot
    : props.selectedEventSnapshot;

  const experienceType = selectedEvent.reservables.experienceType;
  const formik = props.extManagedFormik || buildFormik(serverDate.iso);

  const isRental = experienceIsRental(experienceType);

  const usersDict = mapBy(props.users, u => u._id);

  const subTabDrawerRef = React.useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<ReservationFormikTab>(props.defaultView || 'details');
  const [activeSubTab, setActiveSubTab] = useState<ReservationFormikSubTab>(
    props.reservation
      ? 'none'
      : formik.values._slot
      ? 'tickets'
      : experienceType === 'rental'
      ? 'date-time'
      : 'tickets'
  );
  const { user } = useUserInfo();
  const { activeProperty } = useActiveProperty();
  const [selectedBreakdownIds, setSelectedBreakdownIds] = useState<string[]>([]);
  const [cartReservations, setCartReservations] = useState<Reservation[]>([]);

  const hourFormat = getHourFormat(activeProperty);

  useEffect(() => {
    fetchRelatedReservations().catch(console.error);
  }, [props.reservation?.updatedAt]);

  async function fetchRelatedReservations() {
    if (!props.reservation) return;
    const related = await getRelatedReservations(activeProperty._id, props.reservation._id);
    setCartReservations(related);
  }

  const subActionsRef = useRef<HTMLDivElement>(null);
  const [subActionsElem, setSubActionsElem] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    setSubActionsElem(subActionsRef.current);
  }, [activeSubTab]);

  const [showDelete, setShowDelete] = useState<{ [key: string]: boolean }>({});
  const [showManualView, setShowManualView] = useState(false);
  const [expandedPendingReqId, setExpandedPendingReqId] = useState<string | undefined>(undefined);
  const [expandedLedgerId, setExpandedLedgerId] = useState<string | undefined>(undefined);
  const [paymentLoading, setPaymentLoading] = useState(false);

  const [confirmSendEmail, setConfirmSendEmail] = useState(false);
  const [emailSent, setEmailSent] = useState<boolean | null>(false);

  function handleEmailClick() {
    if (!props.reservation) return;

    if (!confirmSendEmail) {
      setConfirmSendEmail(true);
      return;
    }

    setEmailSent(null);
    try {
      sendConfirmationEmail(props.reservation);
      setEmailSent(true);
    } catch (err) {
      setEmailSent(false);
      throw err;
    }
  }

  const terminalRegistrationIds = (
    activeProperty.reservablesConfig?.terminalRegistrations ?? []
  ).map(tr => tr.readerId);
  const terminalRegistrationMap = mapBy(
    activeProperty.reservablesConfig?.terminalRegistrations ?? [],
    tr => tr.readerId
  );

  const [selectedTerminalId, setSelectedTerminalId] = useState<string | undefined>(
    terminalRegistrationIds[0]
  );

  const { toggleScroll } = useBubbleDrawerTools();

  useEffect(() => {
    toggleScroll(activeSubTab === 'none');
  }, [activeSubTab]);

  const actionClasses = [];
  if (props.actionsAppearance === 'drawer-float')
    actionClasses.push('sticky mt-3 bottom-0 z-20 -mx-6 p-6 pt-4 form-blur flex items-start');
  if (props.actionsClassName) actionClasses.push(props.actionsClassName);

  useImperativeHandle(ref, () => ({
    reset: () => formik.resetForm(),
    submit: () => formik.submitForm(),
  }));

  const subnavClasses = ['flex items-center mb-6'];
  if (!props.reservation) subnavClasses.push('hidden');
  if (props.subnavClassName) subnavClasses.push(props.subnavClassName);

  const { values, errors, touched, setFieldValue } = formik;

  const readOnly = props.type === 'view';

  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({
    [values.tickets[0]?._id || '']: true,
  });

  useEffect(() => {
    if (!props.reservation && values.rental && isRental) {
      // new reservation based on slot - we can prepoulate tickets due to preconditions
      const rentalTicket = selectedEvent.reservables.tickets.find(t =>
        safeIdCompare(t._id, values.rental)
      );

      if (values.tickets.length < 1 && rentalTicket) {
        values.tickets.push({
          _id: new ObjectId().toString(),
          ticketId: rentalTicket._id,
          values: {},
          _ticket: rentalTicket,
        });
        setExpanded({ ...expanded, [values.tickets[0]._id]: true });
      }
    }
  }, []);

  const parsedPhone = phone(values.customer.phone);
  const priceOverrides = values.priceOverrides ?? props.reservation?.priceOverrides;
  const selectedEventTicketsMap = mapBy(selectedEvent.reservables.tickets, t => t._id);
  const priceOverrideSummary = Object.entries(priceOverrides || {}).map(([ticketId, override]) => {
    const ticket = selectedEventTicketsMap[ticketId];
    const basePrice = ticket ? ` (base ${formatCentsToUSD(ticket.price)})` : '';
    return `${
      ticketId === 'all' ? 'All tickets' : ticket?.name ?? 'Unknown ticket'
    }: ${formatCentsToUSD(override)}${basePrice}`;
  });

  const largeCards: Array<{
    key: ReservationFormikSubTab;
    title: string;
    icon: IconProp;
    priceTotal: number;
    hasErrors: boolean;
    summary: string;
  }> = [
    {
      key: 'tickets',
      title: 'Tickets',
      icon: CUSTOM_ICONS['solid-ticket-pen'],
      hasErrors: !!formik.errors.tickets,
      priceTotal: values.tickets.reduce((sum, t) => {
        const ticketPrice = getTicketPriceAfterOverrides(t._ticket, priceOverrides);
        return sum + ticketPrice;
      }, 0),
      summary:
        Object.values(groupBy(values.tickets, rt => rt._ticket.name))
          .map(g => `${g[0]._ticket.name} (${g.length})`)
          .join('<br />') || '<span class="italic text-smoke">None</span>',
    },
    {
      key: 'packages',
      title: 'Packages',
      icon: CUSTOM_ICONS['solid-box-open-pen'],
      hasErrors: !!formik.errors.packages,
      priceTotal: values.packages.reduce((sum, p) => {
        const pkgPrice = p._package.pricePerTicket
          ? values.tickets.length * p._package.price
          : p._package.price;
        return sum + pkgPrice * p.quantity;
      }, 0),
      summary:
        values.packages.map(rp => `${rp._package.name} (${rp.quantity})`).join('<br />') ||
        '<span class="italic text-smoke">None</span>',
    },
    {
      key: 'addons',
      title: 'Addons',
      icon: CUSTOM_ICONS['solid-puzzle-piece-pen'],
      hasErrors: !!formik.errors.addons,
      priceTotal: values.addons.reduce((sum, a) => {
        const addonPrice = a._addon.pricePerTicket
          ? values.tickets.length * a._addon.price
          : a._addon.price;
        return sum + addonPrice * a.quantity;
      }, 0),
      summary:
        values.addons.map(ra => `${ra._addon.name} (${ra.quantity})`).join('<br />') ||
        '<span class="italic text-smoke">None</span>',
    },
  ];

  function scrollSubTabToBottom() {
    setTimeout(() => subTabDrawerRef.current?.scrollTo({ top: 99999, behavior: 'smooth' }));
  }

  const [reservableDetails, setReservableDetails] =
    useState<ReservableEventWithTimeslots | undefined>(undefined);
  const [reservableDetailsLoading, setReservableDetailsLoading] =
    useState<number | null | undefined>(null);

  async function fetchReservableDetails() {
    setReservableDetailsLoading(null);
    try {
      const details = await findReservableDetails(activeProperty._id, selectedEvent.eventTemplate);
      setReservableDetails(details);

      if (values.additionalOccurrences?.length === 0 && values._slot) {
        setFieldValue(
          'additionalOccurrences',
          buildDayspanSubsequentSlots(values._slot).map(dayspanSlotToFormikOccurrence)
        );
      }
      setReservableDetailsLoading(Date.now());
    } catch (err) {
      setReservableDetailsLoading(undefined);
      throw err;
    }
  }

  useEffect(() => {
    fetchReservableDetails().catch(console.error);
  }, [selectedEvent.eventTemplate]);

  const ticketOptions = selectedEvent.reservables.tickets;
  const packageOptions = selectedEvent.reservables.packages;
  const addonOptions = selectedEvent.reservables.addons;

  const ticketOptionsMap = mapBy(ticketOptions, t => t._id);

  const hasTickets = ticketOptions.length > 0;
  const hasPackages = packageOptions.length > 0;
  const hasAddons = addonOptions.length > 0;

  function addNewTicket(action: DropdownItem) {
    const ticketOption = ticketOptionsMap[action.key!];
    if (!ticketOption) return;

    let ticketValues: { [key: string]: string } = {};
    ticketOption.fields.forEach(sch => {
      ticketValues[keyFromLabel(sch.label)] = '';
    });

    const newTicketEntry: ReservationFormikTickets = {
      _id: new ObjectId().toString(),
      ticketId: ticketOption._id,
      values: ticketValues,
      _ticket: ticketOption,
    };

    const newExpanded = { ...expanded };
    values.tickets.forEach(t => {
      newExpanded[t._id] = false;
    });
    newExpanded[newTicketEntry._id] = true;

    setExpanded(newExpanded);
    setFieldValue('tickets', [...values.tickets, newTicketEntry]);
    scrollSubTabToBottom();
  }

  const paymentFormik = useFormik<ReservationPaymentForm>({
    initialValues: {
      type: 'Charge',
      method: 'Stripe',
      amount: 0,
      notes: '',
    },
    validate: async values => {
      let errors: FormikErrors<ReservationPaymentForm> = {};

      const [_, maxRefundAmount] = getReservationAvailableFundsForRefund(props.reservation);

      const basicSchema = Yup.object({
        notes: Yup.string().required('Notes are required'),
        amount: Yup.number()
          .test(
            'min-charge-only',
            'Amount must be at least $1',
            amount => values.type !== 'Charge' || (amount ?? 0) >= 100
          )
          .max(
            values.type === 'Refund' && values.method === 'Stripe'
              ? maxRefundAmount
              : Yup.ref('amount'),
            `Amount cannot exceed ${formatCentsToUSD(maxRefundAmount)}`
          )
          .required('Amount is required'),
      });

      try {
        await basicSchema.validate(values, { abortEarly: false });
      } catch (err) {
        const t = yupToFormErrors<ReservationPaymentForm>(err);
        errors = { ...errors, ...t };
      }

      return errors;
    },
    onSubmit: async () => {
      setPaymentLoading(true);

      try {
        await handleMakeManualPayment();
        paymentFormik.resetForm();
        setShowManualView(false);
      } finally {
        if (props.reservation) {
          props.onRefreshReservation?.(props.reservation);
        }
        setPaymentLoading(false);
      }
    },
  });

  const paymentTypes: ReservationPaymentForm['type'][] = ['Charge', 'Refund'];

  const paymentOpts: ReservePaymentMethod[] =
    paymentFormik.values.type === 'Charge'
      ? ['Stripe', 'StripeTerminal', 'Cash', 'Miscellaneous']
      : paymentFormik.values.type === 'Refund'
      ? ['Stripe', 'Cash', 'Miscellaneous']
      : [];

  useEffect(() => {
    paymentFormik.setFieldValue('method', paymentOpts[0]);
  }, [paymentFormik.values.type]);

  function paymentOptsLabel(opt: ReservePaymentMethod, type: ReservePaymentType) {
    switch (opt) {
      case 'Stripe':
        return type === 'Charge' ? 'Checkout (Stripe)' : 'Stripe';
      case 'StripeTerminal':
        return 'Terminal (Stripe)';
      default:
        return camelToSentenceCase(opt);
    }
  }

  async function handleMakeManualPayment() {
    if (!props.reservation) return;

    const { values } = paymentFormik;

    const newReservation = await createManualPayment(props.reservation, {
      type: values.type,
      method: values.method,
      amount: values.amount,
      notes: values.notes,
    });
    props.onRefreshReservation?.(newReservation);
  }

  async function handleOpenTerminalPayment(paymentIntentId: string, readerId: string) {
    if (!props.reservation) return;
    setPaymentLoading(true);
    try {
      await openTerminalPayment(props.reservation, paymentIntentId, readerId);
    } finally {
      setPaymentLoading(false);
    }
  }

  async function handleShowTerminalDisplay(paymentIntentId: string, readerId: string) {
    if (!props.reservation) return;
    setPaymentLoading(true);
    try {
      await showTerminalDisplay(props.reservation, paymentIntentId, readerId);
    } finally {
      setPaymentLoading(false);
    }
  }

  async function handleCancelTerminalPayment(readerId: string) {
    if (!props.reservation) return;
    setPaymentLoading(true);
    try {
      await cancelTerminalPayment(props.reservation, readerId);
    } finally {
      setPaymentLoading(false);
    }
  }

  async function handleDeleteManualPayment(paymentId: string) {
    if (!props.reservation) return;

    const newReservation = await deleteManualPayment(props.reservation, paymentId);
    props.onRefreshReservation?.(newReservation);
  }

  async function handlePatchPaymentNotes(paymentId: string, notes: string) {
    if (!props.reservation) return;

    const newReservation = await changePaymentNotes(props.reservation, paymentId, notes);
    props.onRefreshReservation?.(newReservation);
    setExpandedLedgerId(undefined);
  }

  const paymentSummary = props.reservation
    ? calculateReservationPricing([props.reservation])?.summaryLineItems
    : [];

  const breakdownItems: PricingBreakdownTableItem[] = paymentSummary.map(item => ({
    id: item.id,
    name: item.label,
    type: item.type,
    price: formatCentsToUSD(item.price),
    quantity: item.quantity,
    total: formatCentsToUSD(item.price * item.quantity),
    totalAsNum: item.price * item.quantity,
    checked: selectedBreakdownIds.includes(item.id),
    _item: item,
  }));

  const breakdownFields: BubbleTableFields<PricingBreakdownTableItem>[] = [
    { key: 'select', label: 'Sum', behavior: BubbleTableFieldBehavior.Condense },
    { key: 'name' },
    { key: 'type' },
    { key: 'price' },
    { key: 'quantity' },
    { key: 'total' },
  ];

  const selectedBreakdownAmtAsNum = breakdownItems
    .filter(item => selectedBreakdownIds.includes(item.id))
    .reduce((acc, item) => acc + item.totalAsNum, 0);

  const selectedBreakdownCount = formatCentsToUSD(selectedBreakdownAmtAsNum);

  const totalBreakdownCount = formatCentsToUSD(
    breakdownItems.reduce((acc, item) => acc + item.totalAsNum, 0)
  );

  const breakdownTemplates: BubbleTableTemplate<PricingBreakdownTableItem>[] = [
    {
      key: 'select',
      render: item => (
        <BubbleInput
          type="checkbox"
          checked={item.checked}
          inputClassName="stretched-link"
          onValue={checked => {
            if (checked) {
              setSelectedBreakdownIds([...selectedBreakdownIds, item.id]);
            } else {
              setSelectedBreakdownIds(selectedBreakdownIds.filter(id => id !== item.id));
            }
          }}
        />
      ),
    },
  ];

  const ledgerItems: PaymentLedgerTableItem[] =
    props.reservation?.payments.map(p => ({
      id: p._id,
      type: p.type,
      method: camelToSentenceCase(p.method),
      date: getLocaleDateTimeStringLocal(p.date),
      amount: formatCentsToUSD(p.amount),
      externalId: p.externalId,
      notes: p.notes,
      systemLog: p.systemLog,
      _item: p,
    })) ?? [];

  const ledgerFields: BubbleTableFields<PaymentLedgerTableItem>[] = [
    { key: 'type', behavior: BubbleTableFieldBehavior.Condense },
    { key: 'method' },
    { key: 'amount' },
    { key: 'date', behavior: BubbleTableFieldBehavior.NoWrap },
    { key: 'externalId' },
    { key: 'notes' },
    { key: 'systemLog' },
    { key: 'actions', label: '', behavior: BubbleTableFieldBehavior.Condense },
  ];

  const ledgerTemplates: Array<BubbleTableTemplate<PaymentLedgerTableItem>> = [
    {
      key: 'type',
      tdProps: makeDefaultLedgerTemplateTdProps,
      render: item => (
        <BubbleBadge
          variant={
            item.type === 'Refund'
              ? 'crimson'
              : item.type === 'Fee'
              ? 'grape'
              : item.type === 'Charge'
              ? 'lime'
              : 'slate'
          }
          className="mt-1"
        >
          {camelToSentenceCase(item.type)}
        </BubbleBadge>
      ),
    },
    { key: 'method', tdProps: makeDefaultLedgerTemplateTdProps },
    {
      key: 'amount',
      tdProps: makeDefaultLedgerTemplateTdProps,
      render: item => (
        <span
          className={`font-semibold ${
            item.type === 'Refund'
              ? 'text-crimson'
              : item.type === 'Charge'
              ? 'text-lime-darkest'
              : item.type === 'Transfer'
              ? 'text-slate'
              : item.type === 'Fee'
              ? ['TaxReversal', 'PlatformReversal'].includes(item._item.method)
                ? 'text-lime-darkest'
                : 'text-crimson'
              : ''
          }`}
        >
          {addGlyphsToPayment(item.amount, item._item.type, item._item.method)}
        </span>
      ),
    },
    {
      key: 'date',
      tdProps: makeDefaultLedgerTemplateTdProps,
      render: item => (
        <>
          {item.date.split(', ').map(e => (
            <div key={e} className="whitespace-nowrap">
              {e}
            </div>
          ))}
        </>
      ),
    },
    {
      key: 'externalId',
      tdProps: makeDefaultLedgerTemplateTdProps,
      render: item => <CopyToClipboardButton copyText={item.externalId} label="Copy External ID" />,
    },
    {
      key: 'notes',
      tdProps: makeDefaultLedgerTemplateTdProps,
    },
    {
      key: 'systemLog',
      tdProps: makeDefaultLedgerTemplateTdProps,
    },
    {
      key: 'actions',
      tdProps: makeDefaultLedgerTemplateTdProps,
      render: item => (
        <BubbleButton
          variant="navy"
          inverse
          className="p-2-i"
          icon="edit"
          onClick={e => {
            e.stopPropagation();
            setExpandedLedgerId(expandedLedgerId === item.id ? undefined : item.id);
          }}
        />
      ),
    },
  ];

  function makeDefaultLedgerTemplateTdProps(
    data: PaymentLedgerTableItem,
    fieldTdProps: React.TdHTMLAttributes<HTMLTableCellElement>
  ) {
    const expanded = expandedLedgerId === data.id;

    return {
      ...fieldTdProps,
      className: [
        fieldTdProps && fieldTdProps.className ? fieldTdProps.className : undefined,
        expanded ? 'bg-bubblegum-lightest' : undefined,
      ]
        .filter(notUndefined)
        .join(' '),
    };
  }

  const pendingItems: PendingPaymentTableItem[] =
    props.reservation?.pendingPayments.map(p => ({
      id: p._id,
      amount: formatCentsToUSD(p.amount),
      date: getLocaleDateTimeStringLocal(p.date),
      url: p.url,
      _item: p,
    })) ?? [];

  const pendingFields: BubbleTableFields<PendingPaymentTableItem>[] = [
    { key: 'amount' },
    { key: 'date', behavior: BubbleTableFieldBehavior.NoWrap },
    { key: 'url', label: 'URL / Terminal', behavior: BubbleTableFieldBehavior.Short },
    {
      key: 'actions',
      label: '',
      behavior: BubbleTableFieldBehavior.Condense,
      render() {
        return (
          <BubbleTooltip
            textRender={() => (
              <div>
                <div className="flex items-center">
                  <FontAwesomeIcon icon="paper-plane" fixedWidth className="mr-1" />
                  <div className="whitespace-nowrap">Process Payment</div>
                </div>
                <div className="flex items-center">
                  <FontAwesomeIcon icon="receipt" fixedWidth className="mr-1" />
                  <div className="whitespace-nowrap">Show Receipt</div>
                </div>
                <div className="flex items-center">
                  <FontAwesomeIcon icon="eraser" fixedWidth className="mr-1" />
                  <div className="whitespace-nowrap">Clear Terminal</div>
                </div>
                <div className="flex items-center">
                  <FontAwesomeIcon icon="trash-alt" fixedWidth className="mr-1" />
                  <div className="whitespace-nowrap">Remove Payment</div>
                </div>
              </div>
            )}
          >
            <div>
              Actions <FontAwesomeIcon icon="info-circle" />
            </div>
          </BubbleTooltip>
        );
      },
    },
  ];

  const pendingTemplates: Array<BubbleTableTemplate<PendingPaymentTableItem>> = [
    {
      key: 'url',
      render: item =>
        item._item.method === 'StripeTerminal' ? (
          <BubbleSelect
            allowUndefined={true}
            undefinedLabel="Select terminal..."
            options={terminalRegistrationIds}
            value={selectedTerminalId}
            labelExtractor={t => terminalRegistrationMap[t]?.label}
            onValue={setSelectedTerminalId}
          />
        ) : (
          <CopyToClipboardButton copyText={item.url} label="Copy Payment Link" />
        ),
    },
    {
      key: 'date',
      render: item => (
        <>
          {item.date.split(', ').map(e => (
            <div key={e} className="whitespace-nowrap">
              {e}
            </div>
          ))}
        </>
      ),
    },
    {
      key: 'actions',
      render: item => {
        return (
          <>
            <BubbleButton
              variant="navy"
              inverse
              className="p-2-i"
              icon={item._item.method === 'StripeTerminal' ? 'paper-plane' : 'qrcode'}
              disabled={item._item.method === 'StripeTerminal' && !selectedTerminalId}
              onClick={() =>
                item._item.method === 'StripeTerminal' && selectedTerminalId
                  ? handleOpenTerminalPayment(item._item.externalId, selectedTerminalId)
                  : setExpandedPendingReqId(expandedPendingReqId === item.id ? undefined : item.id)
              }
            />
            {item._item.method === 'StripeTerminal' && (
              <BubbleButton
                variant="slate"
                inverse
                className="p-2-i"
                icon="receipt"
                disabled={!selectedTerminalId}
                onClick={() =>
                  selectedTerminalId &&
                  handleShowTerminalDisplay(item._item.externalId, selectedTerminalId)
                }
              />
            )}
            {item._item.method === 'StripeTerminal' && (
              <BubbleButton
                variant="slate"
                inverse
                className="p-2-i"
                icon="eraser"
                disabled={!selectedTerminalId}
                onClick={() =>
                  selectedTerminalId && handleCancelTerminalPayment(selectedTerminalId)
                }
              />
            )}
            {!showDelete[item.id] && (
              <BubbleButton
                className="p-2-i"
                variant="crimson"
                inverse
                onClick={() => {
                  setShowDelete({ ...showDelete, [item.id]: true });
                }}
                icon="trash-alt"
              />
            )}
            {showDelete[item.id] && (
              <>
                <BubbleButton
                  className="p-2-i"
                  variant="crimson"
                  inverse
                  onClick={async () => {
                    try {
                      if (item._item.method === 'StripeTerminal' && selectedTerminalId) {
                        await handleCancelTerminalPayment(selectedTerminalId);
                      }
                    } finally {
                      handleDeleteManualPayment(item._item._id);
                    }
                  }}
                  icon="trash-check"
                />
                <BubbleButton
                  className="p-2-i"
                  variant="slate"
                  inverse
                  onClick={() => {
                    setShowDelete({ ...showDelete, [item.id]: false });
                  }}
                  icon="trash-xmark"
                />
              </>
            )}
          </>
        );
      },
    },
  ];

  interface CartTableItem {
    id: string;
    event: string;
    date: string;
    time: string;
    host?: string;
    rental?: string;
    reservationCode: string;
    analytics: ReservationAnalytics;
    _item: Reservation;
  }

  const cartItems: CartTableItem[] = cartReservations.map(r => {
    const ticketsById = mapBy(r.eventSnapshot.reservables.tickets, '_id');

    const summary = summarizeOccurrences(
      [r, ...(r.additionalOccurrences ?? [])],
      ticketsById,
      usersDict,
      { hourFormat, condenseDates: true }
    );

    return {
      id: r._id,
      event: r.eventSnapshot.title,
      host: summary.hosts,
      rental: summary.rentals,
      date: summary.dates,
      time: summary.timeStrings,
      reservationCode: r.reservationCode,
      analytics: calculateReservationAnalytics([r]),
      _item: r,
    };
  });

  const cartFields: BubbleTableFields<CartTableItem>[] = [
    { key: 'event' },
    { key: 'reservationCode', label: 'reservation' },
    { key: 'date' },
    { key: 'time' },
    {
      key: 'host',
      label: 'Rental / Host',
    },
    { key: 'revenue' },
    { key: 'actions', label: '', behavior: BubbleTableFieldBehavior.Condense },
  ];

  const cartTemplates: BubbleTableTemplate<CartTableItem>[] = [
    {
      key: 'host',
      render: ({ host, rental }) =>
        (host || rental) && (
          <div>
            {rental && <div>{rental}</div>}
            <div className={`${rental ? 'text-xs text-slate' : ''}`}>{host}</div>
          </div>
        ),
    },
    {
      key: 'revenue',
      render: data => (
        <span
          className={`${
            data.analytics.totalRevenue === 0
              ? ''
              : data.analytics.totalRevenue > 0
              ? 'font-bold text-lime-darkest'
              : 'font-bold text-crimson'
          }`}
        >
          {formatCentsToUSD(data.analytics.totalRevenue)}
        </span>
      ),
    },
    {
      key: 'actions',
      render: item => (
        <BubbleButton
          variant="navy"
          inverse
          className="p-2-i"
          icon="edit"
          onClick={() => {
            setActiveTab('details');
            props.onNavigateToReservation?.(item._item);
          }}
        />
      ),
    },
  ];

  const occurrences: ReservationFormikOccurrence[] = [
    {
      date: values.date,
      time: values.time,
      timeDisplay: values.timeDisplay,
      duration: values.duration,
      host: values.host,
      rental: values.rental,
    },
    ...(values.additionalOccurrences ?? []),
  ];

  const savedReservationSlotKeys = new Set<string>(
    props.reservation
      ? [
          slotKeyFromReservation(props.reservation),
          ...(props.reservation.additionalOccurrences ?? []).map(existing =>
            buildReserveSlotKey(
              {
                date: dateToIsoUTC(existing.date),
                rental: existing.rental,
                host: existing.host,
              },
              existing.time,
              existing.duration
            )
          ),
        ].filter(notFalsy)
      : []
  );

  const { hasErrors: needsAttention, errors: reservationErrors } = checkReservationErrors({
    experienceType,
    tickets: values.tickets.map(t => t._ticket),
    rental: values.rental,
    occurrences: occurrences.map(occ => ({ ...occ, date: isoToDateUTC(occ.date) })),
    serverDate,
    savedSlotKeys: savedReservationSlotKeys,
    getSlotStatus:
      reservableDetails === undefined
        ? undefined
        : slotKey => reservableDetails.timeslots.find(t => t.slotKey === slotKey),
  });

  const paymentDisabled =
    !paymentFormik.dirty || !paymentFormik.isValid || paymentFormik.isSubmitting;

  const serviceFee =
    selectedBreakdownAmtAsNum > 0
      ? selectedBreakdownAmtAsNum *
          ((activeProperty.reservablesConfig?.serviceFeePercentage ?? 0) / 100) +
        (activeProperty.reservablesConfig?.serviceFeeFlatAmountCents ?? 0)
      : 0;

  const allOccurrences = [
    isIsoDate(values.date)
      ? {
          ...values,
          date: isoToDateUTC(values.date),
        }
      : undefined,
    ...(values.additionalOccurrences ?? []).map(occ =>
      isIsoDate(occ.date) ? { ...occ, date: isoToDateUTC(occ.date) } : undefined
    ),
  ].filter(notUndefined);

  const {
    dates: readonlyDates,
    startTimes: readonlyStartTimes,
    endTimes: readonlyEndTimes,
    timeDisplayStrings: readonlyTimeLabels,
    rentals: readonlyRentals,
    hosts: readonlyHosts,
  } = summarizeOccurrences(allOccurrences, ticketOptionsMap, usersDict, {
    hourFormat,
    condenseDates: true,
  });

  return (
    <FormikProvider value={formik}>
      <div className="ReservationFormik">
        <div className={subnavClasses.join(' ')}>
          <BubbleButton
            block
            appearance="squared"
            variant={activeTab === 'details' ? 'navy' : 'smoke'}
            onClick={() => setActiveTab('details')}
          >
            Details
          </BubbleButton>
          <BubbleButton
            block
            appearance="squared"
            variant={activeTab === 'payments' ? 'navy' : 'smoke'}
            onClick={() => setActiveTab('payments')}
          >
            Payment
          </BubbleButton>
          {cartReservations.length > 0 && (
            <BubbleButton
              block
              appearance="squared"
              variant={activeTab === 'cart' ? 'navy' : 'smoke'}
              onClick={() => setActiveTab('cart')}
            >
              Cart
            </BubbleButton>
          )}
        </div>
        <Form>
          {needsAttention && (
            <BubbleAlert className="mb-3" variant="banana">
              Verify that the reservation details are correct. Mouse over the highlighted fields to
              see what needs attention.
            </BubbleAlert>
          )}
          {props.reservation && !canManageReservation(user, props.reservation) && (
            <BubbleAlert className="mb-3" variant="ocean">
              You have view-only access. Contact a Reserve Admin to make changes.
            </BubbleAlert>
          )}

          {activeTab === 'details' && (
            <div className="grid grid-cols-2 gap-4">
              <BubbleOverlay
                loading={reservableDetailsLoading === null}
                className="col-span-2 bg-white border rounded shadow overflow-clip"
              >
                <div className="p-1">
                  <BubbleButton
                    variant="white"
                    className="text-left border border-navy"
                    block
                    onClick={() => setActiveSubTab('date-time')}
                    iconSuffix="calendar-lines-pen"
                    iconClasses="ml-auto"
                    contentClassName="flex items-center text-base"
                    disabled={readOnly}
                  >
                    {reservationErrors.mismatchingHostsOrRentals.active && (
                      <BubbleTooltip text={reservationErrors.mismatchingHostsOrRentals.message}>
                        <FontAwesomeIcon icon="exclamation-triangle" className="mr-2 text-banana" />
                      </BubbleTooltip>
                    )}
                    {reservationErrors.mismatchingTimes.active && (
                      <BubbleTooltip text={reservationErrors.mismatchingTimes.message}>
                        <FontAwesomeIcon icon="exclamation-triangle" className="mr-2 text-banana" />
                      </BubbleTooltip>
                    )}
                    {reservationErrors.timeslotMissing.active && (
                      <BubbleTooltip text={reservationErrors.timeslotMissing.message}>
                        <FontAwesomeIcon icon="exclamation-triangle" className="mr-2 text-banana" />
                      </BubbleTooltip>
                    )}
                    {reservationErrors.isOverbooked.active && (
                      <BubbleTooltip text={reservationErrors.isOverbooked.message}>
                        <FontAwesomeIcon icon="exclamation-triangle" className="mr-2 text-banana" />
                      </BubbleTooltip>
                    )}
                    {reservationErrors.willOverbookIt.active && (
                      <BubbleTooltip text={reservationErrors.willOverbookIt.message}>
                        <FontAwesomeIcon icon="exclamation-triangle" className="mr-2 text-banana" />
                      </BubbleTooltip>
                    )}
                    {reservationErrors.wrongDayspan.active && (
                      <BubbleTooltip text={reservationErrors.wrongDayspan.message}>
                        <FontAwesomeIcon icon="exclamation-triangle" className="mr-2 text-banana" />
                      </BubbleTooltip>
                    )}
                    <span className="mr-auto">Date &amp; Time</span>
                  </BubbleButton>
                </div>
                <hr />
                <div className="grid grid-cols-6 gap-4 p-4">
                  <BubbleFormGroup label="Date" asterisk className="col-span-2">
                    <DefaultedText val={readonlyDates} />
                  </BubbleFormGroup>
                  <BubbleFormGroup label="Start Time" asterisk className="col-span-2">
                    <DefaultedText val={readonlyStartTimes} />
                  </BubbleFormGroup>
                  <BubbleFormGroup label="End Time" className="col-span-2">
                    <DefaultedText val={readonlyEndTimes} />
                  </BubbleFormGroup>
                  {isRental && (
                    <BubbleFormGroup label="Rental" className="col-span-2">
                      <DefaultedText val={readonlyRentals} />
                    </BubbleFormGroup>
                  )}
                  <BubbleFormGroup label="Host" className={isRental ? 'col-span-2' : 'col-span-3'}>
                    <DefaultedText val={readonlyHosts} />
                  </BubbleFormGroup>
                  <BubbleFormGroup
                    label="Time Display"
                    className={isRental ? 'col-span-2' : 'col-span-3'}
                  >
                    <DefaultedText val={readonlyTimeLabels} />
                  </BubbleFormGroup>
                  <BubbleFormGroup label="Price Overrides" className="col-span-6">
                    {priceOverrideSummary.length > 0 ? (
                      <div className="space-y-1">
                        {priceOverrideSummary.map(summary => (
                          <div key={summary}>{summary}</div>
                        ))}
                      </div>
                    ) : (
                      <span className="italic text-smoke">None</span>
                    )}
                  </BubbleFormGroup>
                </div>
              </BubbleOverlay>
              <div className="col-span-2 grid grid-cols-3 gap-4">
                {largeCards.map(card => (
                  <div key={card.key} className="bg-white rounded overflow-clip shadow">
                    <div className="p-1">
                      <BubbleButton
                        variant="white"
                        className="text-left border border-navy"
                        block
                        onClick={() => setActiveSubTab(card.key)}
                        iconSuffix={card.icon}
                        contentClassName="flex items-center text-base"
                        disabled={readOnly}
                      >
                        {card.hasErrors && (
                          <BubbleTooltip text="There are errors or incomplete fields in this section.">
                            <FontAwesomeIcon
                              icon="exclamation-triangle"
                              className="mr-2 text-crimson"
                            />
                          </BubbleTooltip>
                        )}
                        {card.key === 'tickets' && reservationErrors.missingRental.active && (
                          <BubbleTooltip text={reservationErrors.missingRental.message}>
                            <FontAwesomeIcon
                              icon="exclamation-triangle"
                              className="mr-2 text-banana"
                            />
                          </BubbleTooltip>
                        )}
                        <span className="mr-auto">{card.title}</span>
                        <span className="mx-2 text-slate text-sm">
                          {formatCentsToUSD(card.priceTotal)}
                        </span>
                      </BubbleButton>
                    </div>
                    <hr />
                    <div className="p-3 h-24 overflow-y-auto">
                      <div dangerouslySetInnerHTML={{ __html: card.summary }} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="col-span-2 bg-white border rounded shadow overflow-clip">
                <div className="p-1">
                  <BubbleButton
                    variant="white"
                    className="text-left border border-navy"
                    block
                    onClick={() => setActiveSubTab('reservation-questions')}
                    iconSuffix="pen"
                    iconClasses="ml-auto"
                    contentClassName="flex items-center text-base"
                    disabled={readOnly}
                  >
                    {!!formik.errors.fields && (
                      <BubbleTooltip text="There are errors or incomplete fields in this section.">
                        <FontAwesomeIcon
                          icon="exclamation-triangle"
                          className="mr-2 text-crimson"
                        />
                      </BubbleTooltip>
                    )}
                    Questions
                  </BubbleButton>
                </div>
                <hr />
                <div className="p-3">
                  <div>
                    {formik.values._fields.map(f => f.label).join(', ') || (
                      <span className="italic text-smoke">None</span>
                    )}
                  </div>
                </div>
              </div>
              <BubbleFormGroup
                label="Customer First Name"
                error={
                  ['edit', 'create'].includes(props.type) &&
                  fme(errors, touched, 'customer.firstName' as keyof ReservationFormikForm)
                }
                asterisk
              >
                <BubbleInput
                  block
                  type="text"
                  formik={[formik, 'customer.firstName']}
                  value={values.customer.firstName}
                  readOnly={readOnly}
                />
              </BubbleFormGroup>
              <BubbleFormGroup
                label="Customer Last Name"
                asterisk
                error={
                  ['edit', 'create'].includes(props.type) &&
                  fme(errors, touched, 'customer.lastName' as keyof ReservationFormikForm)
                }
              >
                <BubbleInput
                  block
                  type="text"
                  formik={[formik, 'customer.lastName']}
                  value={values.customer.lastName}
                  readOnly={readOnly}
                />
              </BubbleFormGroup>
              <BubbleFormGroup
                label="Customer Email"
                labelFn={props => (
                  <div className="flex items-center">
                    <div className="mr-auto">
                      {props.label}
                      <span className="text-crimson">&nbsp;*</span>
                    </div>
                    <div className="text-xs text-smoke italic">
                      {!values.customer.email || isValidEmail(values.customer.email)
                        ? values.customer.email
                        : 'Invalid Email'}
                    </div>
                  </div>
                )}
                error={
                  ['edit', 'create'].includes(props.type) &&
                  fme(errors, touched, 'customer.email' as keyof ReservationFormikForm)
                }
              >
                <BubbleInput
                  block
                  type="email"
                  formik={[formik, 'customer.email']}
                  value={values.customer.email}
                  readOnly={readOnly}
                />
                {props.reservation && (
                  <BubbleOverlay loading={emailSent === null} className="mt-3">
                    <BubbleButton
                      onClick={handleEmailClick}
                      variant={confirmSendEmail && !emailSent ? 'crimson' : 'slate'}
                      icon="envelope"
                      disabled={!!emailSent || formik.dirty}
                    >
                      {emailSent
                        ? 'Email Sent!'
                        : !confirmSendEmail
                        ? formik.dirty
                          ? 'Pending Save or Reset...'
                          : 'Send Confirmation Email'
                        : 'Are you sure? Click to Send'}
                    </BubbleButton>
                    {!emailSent && confirmSendEmail && (
                      <BubbleButton
                        variant="slate"
                        inverse
                        onClick={() => setConfirmSendEmail(false)}
                      >
                        Cancel
                      </BubbleButton>
                    )}
                  </BubbleOverlay>
                )}
              </BubbleFormGroup>
              <BubbleFormGroup
                label="Customer Phone"
                labelFn={props => (
                  <div className="flex items-center">
                    <div className="mr-auto">
                      <span>{props.label}</span>
                      <span className="text-crimson">&nbsp;*</span>
                    </div>
                    <div className="text-xs text-smoke italic">
                      {!values.customer.phone || parsedPhone.isValid
                        ? parsedPhone.phoneNumber
                        : 'Invalid Phone'}
                    </div>
                  </div>
                )}
                error={
                  ['edit', 'create'].includes(props.type) &&
                  fme(errors, touched, 'customer.phone' as keyof ReservationFormikForm)
                }
              >
                <BubbleInput
                  block
                  type="tel"
                  formik={[formik, 'customer.phone']}
                  value={values.customer.phone}
                  readOnly={readOnly}
                />
              </BubbleFormGroup>
            </div>
          )}
          {activeTab === 'payments' && props.reservation && (
            <div className="grid grid-cols-1 gap-4">
              {props.type === 'edit' && formik.dirty && (
                <BubbleAlert variant="banana">
                  Pricing breakdown is based on the last saved changes. Save the reservation for
                  prices to update.
                </BubbleAlert>
              )}
              <div>
                <div className="flex items-center">
                  <h2 className="text-xl mb-2 text-slate">Pricing Breakdown</h2>
                  <div className="ml-auto">
                    {selectedBreakdownCount} / {totalBreakdownCount}
                  </div>
                </div>
                <BubbleTable
                  headVariant="turquoise"
                  striped
                  items={breakdownItems}
                  fields={breakdownFields}
                  templates={breakdownTemplates}
                  responsive
                />
                {serviceFee > 0 && (
                  <div className="text-smoke italic mt-1">
                    The service fee for the selected items is{' '}
                    <span className="font-bold">{formatCentsToUSD(serviceFee)}</span>
                  </div>
                )}
              </div>
              {props.reservation && canManageReservation(user, props.reservation) && (
                <div className="mt-4">
                  <div className="flex items-center">
                    <h2 className="text-xl mb-2 text-slate">Pending Payments</h2>
                    <div className="ml-auto">
                      {formatCentsToUSD(
                        pendingItems.reduce((acc, item) => acc + item._item.amount, 0)
                      )}
                    </div>
                  </div>
                  <BubbleOverlay loading={paymentLoading}>
                    <BubbleTable
                      headVariant="turquoise"
                      striped
                      items={pendingItems}
                      fields={pendingFields}
                      templates={pendingTemplates}
                      detailsKey={expandedPendingReqId}
                      detailsClassName="p-2"
                      detailsRender={itm => <PendingTableDetailsRender {...itm} />}
                      responsive
                    />
                  </BubbleOverlay>
                  {!showManualView && (
                    <BubbleButton
                      onClick={() => {
                        setShowManualView(true);
                      }}
                      className="mt-1"
                      variant="navy"
                      inverse
                      block
                      icon="plus"
                    >
                      Add Charge / Refund
                    </BubbleButton>
                  )}
                </div>
              )}
              {showManualView && (
                <div className="bg-white -mx-8 px-8 py-6 border-t border-b">
                  <div className="mb-5 flex items-start">
                    <h2 className="text-2xl text-navy">
                      New {capitalize(paymentFormik.values.type)}
                    </h2>
                    <BubbleButton
                      className="ml-auto"
                      variant="crimson"
                      inverse
                      onClick={() => setShowManualView(false)}
                    >
                      Cancel & Close
                    </BubbleButton>
                  </div>
                  <div className="grid grid-cols-1 gap-4">
                    <BubbleFormGroup label="Type">
                      <BubbleCheckboxGroup
                        isSingleMode
                        options={paymentTypes}
                        value={paymentFormik.values.type}
                        labelExtractor={capitalize}
                        name="type"
                        onValue={v => paymentFormik.setFieldValue('type', v)}
                        onBlur={paymentFormik.handleBlur}
                      />
                    </BubbleFormGroup>
                    <BubbleFormGroup label="Method">
                      <BubbleSelect
                        block
                        allowUndefined={false}
                        labelExtractor={o => paymentOptsLabel(o, paymentFormik.values.type)}
                        options={paymentOpts}
                        value={paymentFormik.values.method}
                        name="method"
                        onValue={v => paymentFormik.setFieldValue('method', v)}
                        onBlur={paymentFormik.handleBlur}
                      />
                    </BubbleFormGroup>
                    <BubbleFormGroup
                      label="Amount"
                      error={fme(paymentFormik.errors, paymentFormik.touched, 'amount')}
                    >
                      <BubbleCurrency
                        block
                        name="amount"
                        value={paymentFormik.values.amount}
                        onValue={v => paymentFormik.setFieldValue('amount', v || 0)}
                        onBlur={paymentFormik.handleBlur}
                      />
                    </BubbleFormGroup>
                    <BubbleFormGroup label="Notes" asterisk>
                      <BubbleTextArea
                        block
                        value={paymentFormik.values.notes}
                        name="notes"
                        onChange={paymentFormik.handleChange}
                        onBlur={paymentFormik.handleBlur}
                      />
                    </BubbleFormGroup>
                    <div className="flex items-center">
                      <div></div>
                      <BubbleOverlay loading={paymentLoading} className="ml-auto">
                        <BubbleButton
                          onClick={() => paymentFormik.handleSubmit()}
                          variant="navy"
                          disabled={paymentDisabled}
                        >
                          Add {capitalize(paymentFormik.values.type)}
                        </BubbleButton>
                      </BubbleOverlay>
                    </div>
                  </div>
                </div>
              )}
              <div>
                <div className="flex items-center">
                  <h2 className="text-xl mb-2 text-slate">Payment Ledger</h2>
                  <div className="ml-auto">
                    {formatCentsToUSD(getReservationRevenue(props.reservation))}
                  </div>
                </div>
                <div className="w-full overflow-x-auto p-1 -m-1">
                  <BubbleTable
                    headVariant="turquoise"
                    striped
                    items={ledgerItems}
                    fields={ledgerFields}
                    templates={ledgerTemplates}
                    detailsKey={expandedLedgerId}
                    detailsClassName="p-2 bg-bubblegum-lightest"
                    detailsRender={itm => (
                      <LedgerDetailsRender
                        item={itm}
                        onSave={handlePatchPaymentNotes}
                        readOnly={readOnly}
                      />
                    )}
                    onRowClick={item =>
                      setExpandedLedgerId(expandedLedgerId === item.id ? undefined : item.id)
                    }
                    responsive
                  />
                </div>
              </div>
            </div>
          )}
          {activeTab === 'cart' && (
            <div>
              <div className="flex items-center">
                <h2 className="text-xl mb-2 text-slate">{props.reservation?.cartCode ?? 'Cart'}</h2>
              </div>
              <BubbleTable
                headVariant="turquoise"
                striped
                items={cartItems}
                fields={cartFields}
                templates={cartTemplates}
                responsive
              />
            </div>
          )}
          <div
            className="absolute top-0 left-0 w-full bg-black opacity-25 cursor-pointer transition-transform"
            onClick={() => setActiveSubTab('none')}
            style={{
              transform: `translateX(${activeSubTab === 'none' ? '100%' : '0'})`,
              zIndex: 21,
              height: '100vh',
            }}
          />
          <div
            ref={subTabDrawerRef}
            className="absolute left-0 bottom-0 z-10 w-full p-6 pb-0 overflow-y-auto bg-eggshell transition-transform"
            style={{
              top: '6vh',
              transform: `translateX(${activeSubTab === 'none' ? '100%' : '0'})`,
              pointerEvents: activeSubTab === 'none' ? 'none' : 'auto',
              height: 'calc(100vh - 6vh)',
              zIndex: 22,
            }}
          >
            {activeSubTab === 'tickets' && (
              <div>
                <div className="flex items-start mb-5">
                  <h2 className="text-2xl text-navy">Tickets</h2>
                  <BubbleButton
                    onClick={() => setActiveSubTab('none')}
                    className="ml-auto"
                    variant="navy"
                    inverse
                    icon="xmark-large"
                    iconSize="xl"
                  />
                </div>
                {hasTickets ? (
                  <>
                    {values.tickets.length < 1 && (
                      <BubbleAlert variant="ocean">
                        No tickets added. Add a ticket by clicking the button below.
                      </BubbleAlert>
                    )}
                    <div className="grid grid-cols-1 gap-4">
                      {values.tickets.map((ticket, i) => (
                        <div key={ticket._id} className="fragment-block">
                          <div
                            className={`fb-header relative ${
                              expanded[ticket._id] ? 'bg-turquoise' : 'bg-smoke'
                            } text-white`}
                          >
                            <div className="flex items-center py-2 ml-5 mr-2">
                              <div className="text-md font-bold mr-auto">
                                {ticket._ticket.name} {i + 1}
                              </div>
                              <div className="text-md font-bold mr-3">
                                {formatCentsToUSD(
                                  getTicketPriceAfterOverrides(ticket._ticket, priceOverrides)
                                )}
                              </div>
                              <div className="flex items-center">
                                {formik.errors.tickets?.[i] && (
                                  <BubbleBadge variant="crimson" className="mr-3">
                                    <FontAwesomeIcon icon="exclamation-triangle" />
                                    <span className="ml-1">Incomplete</span>
                                  </BubbleBadge>
                                )}
                                <BubbleButton
                                  onClick={() => {
                                    setExpanded({
                                      ...expanded,
                                      [ticket._id]: !expanded[ticket._id],
                                    });
                                  }}
                                  variant="white"
                                  inverse
                                  icon="edit"
                                />
                                <BubbleButton
                                  onClick={() => {
                                    const newTickets = [...values.tickets];
                                    newTickets.splice(i, 1);
                                    setFieldValue('tickets', newTickets);
                                  }}
                                  variant="white"
                                  inverse
                                  icon="trash-alt"
                                />
                              </div>
                            </div>
                          </div>
                          {expanded[ticket._id] && (
                            <div className="p-5">
                              <ReservationQuestionFiller
                                formik={formik}
                                values={formik.values.tickets[i].values}
                                formikPath={`tickets[${i}].values`}
                                fields={values.tickets[i]._ticket.fields}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    {props.type !== 'view' && (
                      <div className={actionClasses.join(' ')}>
                        <div className="dropdown-inline-fix">
                          <Dropdown
                            items={ticketOptions.map(t => ({
                              key: t._id,
                              element: `${t.name} - ${formatCentsToUSD(
                                getTicketPriceAfterOverrides(t, priceOverrides)
                              )}`,
                            }))}
                            alignTop
                            onSelect={addNewTicket}
                          >
                            {({ toggleRef, ...rest }) => (
                              <BubbleButton
                                buttonRef={toggleRef}
                                {...rest}
                                icon="plus"
                                variant="slate"
                                inverse
                              >
                                Add a Ticket
                              </BubbleButton>
                            )}
                          </Dropdown>
                        </div>
                        <div className="ml-auto" />
                        <BubbleButton variant="navy" onClick={() => setActiveSubTab('none')}>
                          Close & Continue
                        </BubbleButton>
                      </div>
                    )}
                  </>
                ) : (
                  <BubbleAlert variant="banana">
                    No tickets are available for this event.
                  </BubbleAlert>
                )}
              </div>
            )}
            {activeSubTab === 'packages' && (
              <div>
                <div className="flex items-start mb-5">
                  <h2 className="text-2xl text-navy">Packages</h2>
                  <BubbleButton
                    onClick={() => setActiveSubTab('none')}
                    className="ml-auto"
                    variant="navy"
                    inverse
                    icon="xmark-large"
                    iconSize="xl"
                  />
                </div>
                {hasPackages ? (
                  <>
                    <ReservationPackageFiller formik={formik} options={packageOptions} />
                    {props.type !== 'view' && (
                      <div className={actionClasses.join(' ')}>
                        <div></div>
                        <div className="ml-auto" />
                        <BubbleButton variant="navy" onClick={() => setActiveSubTab('none')}>
                          Close & Continue
                        </BubbleButton>
                      </div>
                    )}
                  </>
                ) : (
                  <BubbleAlert variant="banana">
                    No packages are available for this event.
                  </BubbleAlert>
                )}
              </div>
            )}
            {activeSubTab === 'addons' && (
              <div>
                <div className="flex items-start mb-5">
                  <h2 className="text-2xl text-navy">Addons</h2>
                  <BubbleButton
                    onClick={() => setActiveSubTab('none')}
                    className="ml-auto"
                    variant="navy"
                    inverse
                    icon="xmark-large"
                    iconSize="xl"
                  />
                </div>
                {hasAddons ? (
                  <>
                    <ReservationAddonFiller formik={formik} options={addonOptions} />
                    {props.type !== 'view' && (
                      <div className={actionClasses.join(' ')}>
                        <div></div>
                        <div className="ml-auto" />
                        <BubbleButton variant="navy" onClick={() => setActiveSubTab('none')}>
                          Close & Continue
                        </BubbleButton>
                      </div>
                    )}
                  </>
                ) : (
                  <BubbleAlert variant="banana">
                    No addons are available for this event.
                  </BubbleAlert>
                )}
              </div>
            )}
            {activeSubTab === 'date-time' && (
              <div>
                <div className="flex items-start mb-5">
                  <h2 className="text-2xl text-navy">Date & Time</h2>
                  <BubbleButton
                    variant="navy"
                    inverse
                    className="ml-auto"
                    disabled={reservableDetailsLoading === null}
                    onClick={() => fetchReservableDetails()}
                    icon="rotate"
                    iconSize="xl"
                  />
                  <BubbleButton
                    onClick={() => setActiveSubTab('none')}
                    variant="navy"
                    inverse
                    icon="xmark-large"
                    iconSize="xl"
                  />
                </div>
                {subActionsElem && (
                  <ReservationDateTimeFiller
                    type={props.type}
                    users={props.users}
                    eventSnapshot={selectedEvent}
                    reservation={props.reservation}
                    reservableDetailsLoading={reservableDetailsLoading === null}
                    reservableDetails={reservableDetails}
                    formik={formik}
                    actionsElem={subActionsElem}
                  />
                )}
                {props.type !== 'view' && (
                  <div className={actionClasses.join(' ')}>
                    <div className="flex-grow" ref={subActionsRef} />
                    <div className="ml-auto" />
                    <BubbleButton variant="navy" onClick={() => setActiveSubTab('none')}>
                      Close & Continue
                    </BubbleButton>
                  </div>
                )}
              </div>
            )}
            {activeSubTab === 'reservation-questions' && (
              <div>
                <div className="flex items-start mb-5">
                  <h2 className="text-2xl text-navy">Reservation Questions</h2>
                  <BubbleButton
                    onClick={() => setActiveSubTab('none')}
                    className="ml-auto"
                    variant="navy"
                    inverse
                    icon="xmark-large"
                    iconSize="xl"
                  />
                </div>
                <ReservationQuestionFiller
                  formik={formik}
                  values={formik.values.fields}
                  formikPath={`fields`}
                  fields={values._fields}
                />
                {props.type !== 'view' && (
                  <div className={actionClasses.join(' ')}>
                    <div className="text-smoke italic"></div>
                    <div className="ml-auto" />
                    <BubbleButton variant="navy" onClick={() => setActiveSubTab('none')}>
                      Close & Continue
                    </BubbleButton>
                  </div>
                )}
              </div>
            )}
          </div>
        </Form>
      </div>
    </FormikProvider>
  );
}

interface LedgerDetailsRenderProps {
  item: PaymentLedgerTableItem;
  onSave: (paymentId: string, notes: string) => Promise<void>;
  readOnly?: boolean;
}

function LedgerDetailsRender(props: LedgerDetailsRenderProps) {
  const { item, onSave, readOnly } = props;
  const [notes, setNotes] = useState(item.notes ?? '');

  async function handleSave() {
    await onSave(item.id, notes);
  }

  return (
    <div className="bg-white p-4 rounded shadow border grid grid-cols-1 gap-4">
      <BubbleFormGroup label="Notes">
        <BubbleTextArea
          block
          value={notes}
          readOnly={readOnly}
          onChange={e => setNotes(e.target.value)}
        />
      </BubbleFormGroup>
      {!readOnly && (
        <div className="flex items-center">
          <div></div>
          <div className="ml-auto"></div>
          <BubbleButton variant="navy" onClick={handleSave}>
            Update Note
          </BubbleButton>
        </div>
      )}
    </div>
  );
}

function PendingTableDetailsRender(item: PendingPaymentTableItem) {
  const [qrCode, setQrCode] = useState<string | undefined>(undefined);

  async function getQrCode() {
    const res = await QRCode.toString(item.url, {
      type: 'svg',
    });
    setQrCode(res);
  }

  useEffect(() => {
    getQrCode();
  }, [item.url]);

  return (
    <div className="shadow p-2 mt-2 rounded border bg-white">
      {qrCode && (
        <div
          className="mx-auto w-full max-w-100"
          dangerouslySetInnerHTML={{ __html: qrCode }}
        ></div>
      )}
    </div>
  );
}

export default forwardRef<ReservationFormikRef, ReservationFormikProps>(ReservationFormik);
